import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from 'constants/endpoints';
import { api } from 'lib/api';
import {
  ArrowLeft,
  Calendar,
  Coins,
  ExternalLink,
  Eye,
  Gamepad2,
  Lock,
  Percent,
  RefreshCw,
  Swords,
  TrendingUp,
  Trophy,
  User,
  Users,
} from 'lucide-react';
import {
  BATTLE_STATUS_LABEL,
  BATTLE_TYPE_LABEL,
  PRIVATE_TABLE_STATUS_LABEL,
  labelFor,
  statusPillByLabel,
} from 'constants/enums';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_EYE_BTN_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';

const money = (symbol: string, v: unknown) =>
  `${symbol}${parseFloat(String(v ?? 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const dateTime = (v: unknown) => (v ? new Date(String(v)).toLocaleString() : '—');

type StatCardProps = {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  iconWrap: string;
};

function StatCard({ icon: Icon, label, value, sub, iconWrap }: StatCardProps) {
  return (
    <div className="rounded-xl border border-line bg-card p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl mb-3 ${iconWrap}`}>
        <Icon size={20} strokeWidth={2} />
      </div>
      <p className="text-[11px] font-bold text-muted uppercase tracking-wider">{label}</p>
      <p className="text-2xl sm:text-3xl font-extrabold text-heading mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted mt-2 leading-snug">{sub}</p>}
    </div>
  );
}

export default function PrivateTableDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchDetails = useCallback(() => {
    if (!id) return;
    setLoading(true);
    api
      .get(API_ENDPOINTS.ADMIN.PRIVATE_TABLES.GET_DETAILS(id))
      .then((res) => {
        if (res.success && res.data) setDetails(res.data);
      })
      .catch((err) => console.error('Failed to fetch private table details:', err))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const battlesListUrl = (table: { id: number; room_code?: string }) => {
    const params = new URLSearchParams({
      privateTableId: String(table.id),
      type: '2',
    });
    if (table.room_code) params.set('roomCode', table.room_code);
    return `/battles?${params.toString()}`;
  };

  if (loading && !details) {
    return (
      <div className="flex h-64 items-center justify-center text-muted font-medium">
        <RefreshCw className="animate-spin mr-2" size={20} /> Loading private table details...
      </div>
    );
  }

  if (!details?.table) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <button
          onClick={() => navigate('/private-tables')}
          className="inline-flex items-center gap-2 text-sm font-bold text-muted hover:text-heading"
        >
          <ArrowLeft size={16} /> Back to Private Tables
        </button>
        <div className="bg-card p-12 rounded-2xl border border-line text-center text-muted font-medium">
          Private table PT-{id} not found or could not be loaded.
        </div>
      </div>
    );
  }

  const table = details.table;
  const battles: any[] = Array.isArray(details.battles) ? details.battles : [];
  const activity = details.activitySummary ?? {};
  const symbol = table.currency_symbol || '';
  const totalBattles = Number(activity.totalBattles ?? 0);
  const completedBattles = Number(activity.completedBattles ?? 0);
  const uniquePlayers = Number(activity.uniquePlayers ?? 0);
  const totalGameVolume = Number(activity.totalGameVolume ?? activity.totalPotAmount ?? 0);
  const totalCommission = Number(activity.totalCommission ?? 0);
  const totalDistributeAmount = Number(activity.totalDistributeAmount ?? 0);
  const commissionEstimated = Boolean(activity.commissionIsEstimated);
  const distributeEstimated = Boolean(activity.distributeIsEstimated);
  const rakePct = Number(activity.houseRakePercentage ?? 0);

  return (
    <div className="space-y-6 animate-fadeIn">
      <button
        onClick={() => navigate('/private-tables')}
        className="inline-flex items-center gap-2 text-sm font-semibold text-muted hover:text-heading transition-colors group"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-card group-hover:bg-subtle transition-colors">
          <ArrowLeft size={15} />
        </span>
        Back to Private Tables
      </button>

      <div className="rounded-2xl border border-line bg-card shadow-sm p-5 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4 min-w-0">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-brand-gold">
              <Lock size={24} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h1 className="text-xl sm:text-2xl font-bold text-heading tracking-tight">
                  {table.table_name || 'Private Table'}
                </h1>
                <span className="font-mono text-sm text-muted">PT-{table.id}</span>
                <span
                  className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full border ${statusPillByLabel(labelFor(PRIVATE_TABLE_STATUS_LABEL, table.status))}`}
                >
                  {labelFor(PRIVATE_TABLE_STATUS_LABEL, table.status)}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted">
                <span className="font-mono font-semibold text-heading">{table.room_code || '—'}</span>
                <span>{table.variant_name || table.variant_code || '—'}</span>
                <span className="inline-flex items-center gap-1">
                  <User size={13} />
                  {table.created_by ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/players/${table.created_by}/enforcement`)}
                      className="text-heading hover:text-brand-gold hover:underline transition-colors"
                      title="Open player details"
                    >
                      {table.created_by_name || `User #${table.created_by}`}
                    </button>
                  ) : (
                    '—'
                  )}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Calendar size={13} />
                  {dateTime(table.created_at)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {table.latest_battle_id ? (
              <button
                type="button"
                onClick={() => navigate(`/battles/${table.latest_battle_id}`)}
                className={LIST_EYE_BTN_CLASS}
                title="View latest battle"
                aria-label="View latest battle"
              >
                <Eye size={16} strokeWidth={2} />
              </button>
            ) : null}
            <button
              type="button"
              onClick={fetchDetails}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line text-muted hover:text-heading hover:bg-subtle transition-colors"
              title="Refresh"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 text-sm">
          {[
            ['Boot amount', money(symbol, table.boot_amount)],
            ['Min / Max bet', `${money(symbol, table.min_bet)} / ${money(symbol, table.max_bet)}`],
            ['Max seats', String(table.max_seats ?? '—')],
            ['Current players', String(table.current_players ?? 0)],
            ['Currency', `${symbol} ${table.currency_code || '—'}`],
            ['Max blind rounds', String(table.max_blind_rounds ?? '—')],
            ['Session ID', table.session_uuid || '—'],
            ['Game variant', `${table.variant_name || '—'} (${table.variant_code || '—'})`],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-line bg-subtle/40 px-3 py-2.5">
              <dt className="text-[10px] font-bold uppercase tracking-wider text-muted">{label}</dt>
              <dd className="mt-1 font-semibold text-heading break-all">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div>
        <h2 className="text-sm font-bold text-heading mb-3">Financial summary</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon={TrendingUp}
            label="Total Game Volume"
            value={money(symbol, totalGameVolume)}
            sub="Sum of peak pot across all battles on this table"
            iconWrap="bg-violet-500/10 text-violet-600"
          />
          <StatCard
            icon={Percent}
            label="Total Commission"
            value={money(symbol, totalCommission)}
            sub={
              commissionEstimated
                ? `Estimated at ${rakePct}% house rake`
                : `Recorded house rake · ${rakePct}% configured`
            }
            iconWrap="bg-rose-500/10 text-rose-700"
          />
          <StatCard
            icon={Coins}
            label="Total Distribute Amount"
            value={money(symbol, totalDistributeAmount)}
            sub={
              distributeEstimated
                ? 'Estimated winnings paid (volume − commission)'
                : 'Total winnings credited to players'
            }
            iconWrap="bg-emerald-500/10 text-emerald-600"
          />
        </div>
      </div>

      <div>
        <h2 className="text-sm font-bold text-heading mb-3">Activity overview</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            icon={Swords}
            label="Total battles"
            value={totalBattles.toLocaleString()}
            sub="Hands played on this table"
            iconWrap="bg-indigo-500/10 text-indigo-600"
          />
          <StatCard
            icon={Trophy}
            label="Completed"
            value={completedBattles.toLocaleString()}
            sub={totalBattles > 0 ? `${Math.round((completedBattles / totalBattles) * 100)}% finished` : 'Finished battles'}
            iconWrap="bg-emerald-500/10 text-emerald-600"
          />
          <StatCard
            icon={Users}
            label="Unique players"
            value={uniquePlayers.toLocaleString()}
            sub="Distinct players seated"
            iconWrap="bg-blue-500/10 text-blue-600"
          />
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
        <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-2">
            <Gamepad2 size={18} className="text-indigo-600" />
            <div>
              <h4 className="font-bold text-heading">Recent battles</h4>
              <p className="text-xs text-muted">Last 10 battles played on this private table</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(battlesListUrl(table))}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-line bg-subtle/60 text-xs font-bold text-heading hover:bg-subtle transition-colors shrink-0"
          >
            View all <ExternalLink size={13} />
          </button>
        </div>
        <div className="overflow-auto max-h-[50vh]">
          <table className={LIST_TABLE_CLASS}>
            <thead className={LIST_THEAD_CLASS}>
              <tr>
                <th className={`${LIST_TH_NO_CLASS}`}>No.</th>
                {['Battle', 'Type', 'Players', 'Rounds', 'Pot', 'Winner', 'Status', 'Played', ''].map((h, i) => (
                  <th key={i} className={LIST_TH_CLASS}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
              {battles.length > 0 ? (
                battles.map((b, index) => (
                  <tr key={b.id} className="hover:bg-subtle/50 transition-colors">
                    <td className={LIST_TD_NO_CLASS}>{listRowNumber(1, index, 0)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap font-bold text-heading`}>BTL-{b.id}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-body`}>
                      {labelFor(BATTLE_TYPE_LABEL, b.battle_type)}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>{b.player_count ?? '—'}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>{b.total_rounds ?? '—'}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap font-bold text-heading`}>
                      {money(b.currency_symbol || symbol, b.pot_amount)}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-body`}>
                      {b.winner_user_id ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/players/${b.winner_user_id}/enforcement`)}
                          className="font-medium text-heading hover:text-brand-gold hover:underline transition-colors"
                          title="Open player details"
                        >
                          {b.winner_username || b.winner_display_name || `User #${b.winner_user_id}`}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <span
                        className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full border ${statusPillByLabel(labelFor(BATTLE_STATUS_LABEL, b.status))}`}
                      >
                        {labelFor(BATTLE_STATUS_LABEL, b.status)}
                      </span>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-xs text-muted`}>{dateTime(b.created_at)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <button
                        type="button"
                        onClick={() => navigate(`/battles/${b.id}`)}
                        className={LIST_EYE_BTN_CLASS}
                        title="View battle"
                        aria-label="View battle"
                      >
                        <Eye size={16} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="px-5 py-10 text-center text-muted font-medium">
                    No battles recorded for this private table yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
